import {
  CreateOrganizationDTOPrimaryVerticalEnum,
  CreateOrganizationDTOSecondaryVerticalsEnum,
  CreateOrganizationDTOTypeEnum,
  InternalTeamMemberDTOTeamEnum,
} from '@reposit/api-client';
import { Formik, FormikProps, useFormikContext } from 'formik';
import React, { useCallback, useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-grid-system';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import * as yup from 'yup';
import { getBreakpoint } from '../../base/style';
import Button from '../../components/Button';
import ConfirmationModal from '../../components/ConfirmationModal';
import FlashMessage from '../../components/FlashMessage';
import { Input, RadioGroup } from '../../components/FormFields';
import Checkbox from '../../components/FormFields/Checkbox';
import FieldWithLabel from '../../components/FormFields/FieldWithLabel';
import Select from '../../components/FormFields/Select';
import { FullPageLoader } from '../../components/Loading';
import RepositCard from '../../components/Reposit/RepositCard';
import Sort from '../../components/Sort';
import Typeahead from '../../components/Typeahead';
import { Header2, P1, P2 } from '../../components/Typography';
import { useCreatedTypeaheadOption } from '../../hooks/use-created-typeahead-option';
import { useIntegrators } from '../../hooks/use-integrators';
import { useInternalTeamMembers } from '../../hooks/use-internal-team-members';
import { useOrganizationGroupSearch } from '../../hooks/use-organization-group-search';
import { createLoadingSelector } from '../../redux/loading/loading.selector';
import { CREATE_ORGANIZATION_STORE_KEY, createOrganizationRequested } from '../../redux/organization/organization.actions';
import { CreateOrganizationPayload } from '../../redux/organization/organization.types';
import {
  CREATE_ORG_GROUP_STORE_KEY,
  createOrganizationGroupRequested,
} from '../../redux/organization-group/organization-group.actions';
import {
  getCreatedOrganizationGroup,
  getCreateOrganizationGroupRequestId,
} from '../../redux/organization-group/organization-group.selectors';
import { formatIntegratorOptions, formatInternalTeamMembersOptions } from '../../utils/common.utils';
import { getGroupPayloadValue, NO_ORGANIZATION_GROUP_VALUE } from '../../utils/organization-group.utils';
import { FLASH_MESSAGE_TIMEOUT, useFlashMessage } from '../FlashMessage';

export const INTEGRATOR_PLACEHOLDER_VALUE = 'PLEASE_SELECT_INTEGRATOR';

const Field = styled.div`
  min-height: 86px;
  @media screen and (min-width: ${getBreakpoint('lg')}) {
    max-width: 94%;
  }
`;

const ActionInfo = styled(P2)`
  margin: 0 10px;
`;

const Actions = styled.div`
  align-items: center;
  display: flex;
  justify-content: flex-end;
  margin: 0 0 140px;
`;

const renderCreateOrganizationGroupOption = (name: string) => (
  <>
    <strong>+ Create New Parent:</strong> "{name}"
  </>
);

enum IS_INTEGRATOR {
  YES = 'YES',
  NO = 'NO',
}

const verticalOptions = [
  { name: CreateOrganizationDTOPrimaryVerticalEnum.INDEPENDENTSINGLE, label: 'Independent Single' },
  { name: CreateOrganizationDTOPrimaryVerticalEnum.BTR, label: 'Build to Rent' },
  { name: CreateOrganizationDTOPrimaryVerticalEnum.INDEPENDENTMULTI, label: 'Independent Multi' },
  { name: CreateOrganizationDTOPrimaryVerticalEnum.FRANCHISE, label: 'Franchise' },
  { name: CreateOrganizationDTOPrimaryVerticalEnum.CORPORATE, label: 'Corporate' },
  { name: CreateOrganizationDTOPrimaryVerticalEnum.PRIVATELANDLORD, label: 'Private Landlord' },
  { name: CreateOrganizationDTOPrimaryVerticalEnum.STUDENT, label: 'Student' },
  { name: CreateOrganizationDTOPrimaryVerticalEnum.PBSA, label: 'PBSA' },
  { name: CreateOrganizationDTOPrimaryVerticalEnum.HMO, label: 'HMO' },
] as const;

interface AddOrganizationForm {
  organizationName: string;
  hubspotId: string;
  type: CreateOrganizationDTOTypeEnum;
  email: string;
  isIntegrator: IS_INTEGRATOR;
  integratorId?: string;
  salesOwnerId?: string;
  primaryVertical: CreateOrganizationDTOPrimaryVerticalEnum;
  secondaryVerticals: CreateOrganizationDTOSecondaryVerticalsEnum[];
  organizationGroupId: string;
  parentOrganizationGroupId: string;
}

type OrganizationGroupFieldName = 'organizationGroupId' | 'parentOrganizationGroupId';

const requiredOrganizationGroup = () =>
  yup.string().when('type', {
    is: (type: CreateOrganizationDTOTypeEnum) => type !== CreateOrganizationDTOTypeEnum.PRIVATE,
    then: yup.string().required('Required').notOneOf([NO_ORGANIZATION_GROUP_VALUE], 'Required'),
    otherwise: yup.string(),
  });

export const ADD_ORGANISATION_VALIDATION_SCHEMA = yup.object().shape({
  organizationName: yup.string().required('Required'),
  type: yup.string().required('Required'),
  email: yup.string().email().required('Required'),
  isIntegrator: yup.string().required('Required'),
  integratorId: yup.string().when('isIntegrator', {
    is: (isIntegrator: IS_INTEGRATOR) => isIntegrator === IS_INTEGRATOR.YES,
    then: yup.string().required('Required').notOneOf([INTEGRATOR_PLACEHOLDER_VALUE], 'Required'),
    otherwise: yup.string(),
  }),
  hubspotId: yup.string(),
  salesOwnerId: yup.string(),
  primaryVertical: yup
    .string()
    .oneOf(verticalOptions.map((v) => v.name))
    .required('Required'),
  secondaryVerticals: yup.array().of(yup.string().oneOf(verticalOptions.map((v) => v.name))),
  organizationGroupId: requiredOrganizationGroup(),
  parentOrganizationGroupId: requiredOrganizationGroup(),
});

const ResetIntegratorFields = () => {
  const { values, setFieldValue } = useFormikContext<AddOrganizationForm>();
  useEffect(() => {
    if (values.isIntegrator === IS_INTEGRATOR.NO) {
      setFieldValue('integratorId', undefined);
      setFieldValue('hubspotId', undefined);
      setFieldValue('salesOwnerId', undefined);
    }
  }, [values.isIntegrator, setFieldValue]);
  return null;
};

const ResetOrganizationGroupFieldsForPrivate = () => {
  const { values, setFieldValue } = useFormikContext<AddOrganizationForm>();
  useEffect(() => {
    if (values.type === CreateOrganizationDTOTypeEnum.PRIVATE) {
      setFieldValue('organizationGroupId', NO_ORGANIZATION_GROUP_VALUE);
      setFieldValue('parentOrganizationGroupId', NO_ORGANIZATION_GROUP_VALUE);
    }
  }, [values.type, setFieldValue]);
  return null;
};

const getCreateOrganizationGroupPayloadValue = (value: string) => {
  const groupId = getGroupPayloadValue(value);
  return groupId === null ? undefined : groupId;
};

const getValuesWithPrivateLandlordVerticalDefault = (
  values: AddOrganizationForm,
  type: CreateOrganizationDTOTypeEnum,
): AddOrganizationForm => {
  const updatedValues = { ...values, type };

  if (type !== CreateOrganizationDTOTypeEnum.PRIVATE) return updatedValues;

  return {
    ...updatedValues,
    primaryVertical: CreateOrganizationDTOPrimaryVerticalEnum.PRIVATELANDLORD,
    secondaryVerticals: values.secondaryVerticals.filter(
      (vertical) => vertical !== CreateOrganizationDTOSecondaryVerticalsEnum.PRIVATELANDLORD,
    ),
  };
};

const AddNewOrganisation: React.FC = () => {
  const dispatch = useDispatch();
  const formikRef = React.useRef<FormikProps<AddOrganizationForm> | null>(null);
  const [pendingOrganization, setPendingOrganization] = useState<CreateOrganizationPayload | undefined>(undefined);
  const [flashMessage, dismissFlashMessage] = useFlashMessage([CREATE_ORGANIZATION_STORE_KEY, CREATE_ORG_GROUP_STORE_KEY]);
  const loadingSelector = createLoadingSelector([CREATE_ORGANIZATION_STORE_KEY]);
  const isCreateLoading = useSelector(loadingSelector);
  const isCreateOrganizationGroupLoading = useSelector(createLoadingSelector([CREATE_ORG_GROUP_STORE_KEY]));
  const createdOrganizationGroup = useSelector(getCreatedOrganizationGroup);
  const createRequestId = useSelector(getCreateOrganizationGroupRequestId);
  const { isLoading: areIntegratorsLoading, integrators } = useIntegrators();
  const {
    isLoading: isFetchOrganizationGroupsLoading,
    organizationGroups,
    searchOrganizationGroups,
  } = useOrganizationGroupSearch();
  const { isLoading: areInternalTeamMembersLoading, internalTeamMembers } = useInternalTeamMembers(
    InternalTeamMemberDTOTeamEnum.SALES,
  );

  const integratorOptions = formatIntegratorOptions(integrators);
  const integratorOptionsWithPlaceholder = [
    { label: 'Please select an integrator', value: INTEGRATOR_PLACEHOLDER_VALUE },
    ...integratorOptions,
  ];
  const selectedOption = integratorOptions.find(
    (option) => pendingOrganization !== undefined && option.value === pendingOrganization.integratorId,
  );
  const pendingIntegratorName = selectedOption ? selectedOption.label : '';
  const internalTeamMemberSalesOptions = formatInternalTeamMembersOptions(internalTeamMembers);
  const isLoading = isCreateLoading || areIntegratorsLoading || areInternalTeamMembersLoading;
  const createOrganizationGroup = useCallback(
    (name: string, requestId: string) => {
      dispatch(
        createOrganizationGroupRequested({
          name,
          requestId,
        }),
      );
    },
    [dispatch],
  );
  const typeaheadCreate = useCreatedTypeaheadOption<AddOrganizationForm, OrganizationGroupFieldName>({
    formikRef,
    createdOption: createdOrganizationGroup,
    createRequestId,
    emptyValue: NO_ORGANIZATION_GROUP_VALUE,
    onCreateOption: createOrganizationGroup,
  });
  const onSubmit = (values: AddOrganizationForm) => {
    const {
      organizationName,
      type,
      email,
      integratorId,
      hubspotId,
      salesOwnerId,
      primaryVertical,
      secondaryVerticals,
      organizationGroupId,
      parentOrganizationGroupId,
    } = values;

    const integratorData =
      values.isIntegrator === IS_INTEGRATOR.YES
        ? {
            integratorId,
          }
        : {};

    const isPrivateLandlord = type === CreateOrganizationDTOTypeEnum.PRIVATE;
    const organizationGroupPayload = isPrivateLandlord
      ? {
          organizationGroupId: undefined,
          parentOrganizationGroupId: undefined,
        }
      : {
          organizationGroupId: getCreateOrganizationGroupPayloadValue(organizationGroupId),
          parentOrganizationGroupId: getCreateOrganizationGroupPayloadValue(parentOrganizationGroupId),
        };

    const data: CreateOrganizationPayload = {
      organizationName,
      type,
      email,
      hubspotId,
      salesOwnerId: salesOwnerId === 'none' ? undefined : salesOwnerId,
      primaryVertical,
      secondaryVerticals,
      ...organizationGroupPayload,
      ...integratorData,
    };

    if (values.isIntegrator === IS_INTEGRATOR.YES) {
      setPendingOrganization(data);
      return;
    }

    dispatch(createOrganizationRequested(data));
  };

  if (isLoading || (!integrators.length && integrators.length !== 0)) return <FullPageLoader />;

  return (
    <>
      <Formik
        innerRef={formikRef}
        initialValues={{
          organizationName: '',
          type: CreateOrganizationDTOTypeEnum.AGENCY,
          email: '',
          isIntegrator: IS_INTEGRATOR.NO,
          integratorId: INTEGRATOR_PLACEHOLDER_VALUE,
          hubspotId: '',
          salesOwnerId: undefined,
          primaryVertical: verticalOptions[0].name,
          secondaryVerticals: [],
          organizationGroupId: NO_ORGANIZATION_GROUP_VALUE,
          parentOrganizationGroupId: NO_ORGANIZATION_GROUP_VALUE,
        }}
        onSubmit={onSubmit}
        validateOnMount
        validationSchema={ADD_ORGANISATION_VALIDATION_SCHEMA}
      >
        {({
          values,
          handleSubmit,
          handleChange,
          handleBlur,
          errors,
          touched,
          isValid,
          setValues,
          setFieldValue,
        }: FormikProps<AddOrganizationForm>) => {
          const isPrivateLandlord = values.type === CreateOrganizationDTOTypeEnum.PRIVATE;
          const orgNameLabel = isPrivateLandlord ? 'Landlord Full Name *' : 'Name *';
          const handleTypeChange = (type: CreateOrganizationDTOTypeEnum) => {
            setValues(getValuesWithPrivateLandlordVerticalDefault(values, type));
          };

          return (
            <form onSubmit={handleSubmit}>
              <ResetIntegratorFields />
              <ResetOrganizationGroupFieldsForPrivate />
              <Container>
                <Row>
                  <Col lg={12}>
                    <div style={{ marginBottom: 40 }}>
                      {flashMessage ? (
                        <FlashMessage
                          payload={flashMessage}
                          onDismiss={dismissFlashMessage}
                          timeRemaining={FLASH_MESSAGE_TIMEOUT}
                        />
                      ) : undefined}
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col lg={12}>
                    <Header2>Add New Organisation</Header2>
                    <P1>
                      Adding a new organisation will create an organisation along with an invite for the associated user.{' '}
                      <span>
                        Please <Link to={'/organisations'}>search</Link> for the organisation first to avoid creating duplicates.
                      </span>
                    </P1>
                    <P1>
                      If an integrator is selected the invite will be automatically accepted and the user onboarded. The
                      integrator will be provided with API credentials for this user, and the user will be notified that the
                      integration has been enabled. The user will be asked to set their password and agree to our T&Cs on initial
                      login.
                    </P1>
                  </Col>
                </Row>
                {integrators.length > 0 ? (
                  <Row>
                    <Col lg={12}>
                      <RepositCard title="Integrator">
                        <Container fluid>
                          <Row>
                            <Col lg={12} style={{ padding: 0 }}>
                              <FieldWithLabel
                                label="Are you onboarding for an integrator?"
                                error={errors.isIntegrator}
                                touched={touched.isIntegrator}
                              >
                                <RadioGroup
                                  name="isIntegrator"
                                  // has to be string here annoyingly
                                  options={[
                                    { value: IS_INTEGRATOR.YES, label: 'Yes' },
                                    { value: IS_INTEGRATOR.NO, label: 'No' },
                                  ]}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={errors.isIntegrator}
                                  touched={touched.isIntegrator}
                                  selected={values.isIntegrator}
                                />
                              </FieldWithLabel>
                            </Col>
                            {values.isIntegrator === IS_INTEGRATOR.YES ? (
                              <Col lg={12} style={{ padding: 0, marginBottom: 10 }}>
                                <FieldWithLabel label="Integrator">
                                  <Sort
                                    label="Integrator"
                                    onSelect={(val) => setFieldValue('integratorId', val)}
                                    options={integratorOptionsWithPlaceholder}
                                    selected={values.integratorId || INTEGRATOR_PLACEHOLDER_VALUE}
                                    hideLabel
                                  />
                                </FieldWithLabel>
                              </Col>
                            ) : null}
                          </Row>
                        </Container>
                      </RepositCard>
                    </Col>
                  </Row>
                ) : null}
                <Row>
                  <Col lg={12}>
                    <RepositCard title="Organisation Details">
                      <Container fluid>
                        <Row>
                          <Col lg={12} style={{ padding: 0 }}>
                            <Field>
                              <FieldWithLabel
                                label={orgNameLabel}
                                touched={touched.organizationName}
                                error={errors.organizationName}
                              >
                                <Input
                                  name="organizationName"
                                  value={values.organizationName}
                                  error={errors.organizationName}
                                  touched={touched.organizationName}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                />
                              </FieldWithLabel>
                            </Field>
                          </Col>
                        </Row>
                        <Row>
                          <Col lg={12} style={{ padding: 0 }}>
                            <Field>
                              <FieldWithLabel label="Hubspot Company ID" touched={touched.hubspotId} error={errors.hubspotId}>
                                <Input
                                  name="hubspotId"
                                  value={values.hubspotId}
                                  error={errors.hubspotId}
                                  touched={touched.hubspotId}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                />
                              </FieldWithLabel>
                            </Field>
                          </Col>
                        </Row>
                        <Row>
                          <Col lg={12} style={{ padding: 0 }}>
                            <Field>
                              <FieldWithLabel label="Type *" touched={touched.type} error={errors.type}>
                                <Select onChange={handleTypeChange} value={values.type}>
                                  <option value={undefined}>Please Select</option>
                                  <option value={CreateOrganizationDTOTypeEnum.AGENCY}>Agency</option>
                                  <option value={CreateOrganizationDTOTypeEnum.BUILDTORENT}>Build to Rent</option>
                                  <option value={CreateOrganizationDTOTypeEnum.PRIVATE}>Private Landlord</option>
                                </Select>
                              </FieldWithLabel>
                            </Field>
                          </Col>
                        </Row>
                        <Row>
                          <Col lg={12} style={{ padding: 0 }}>
                            <Field>
                              <FieldWithLabel
                                label="Primary Vertical *"
                                touched={touched.primaryVertical}
                                error={errors.primaryVertical}
                              >
                                <Select
                                  onChange={(vertical) => {
                                    setValues({
                                      ...values,
                                      primaryVertical: vertical,
                                      secondaryVerticals: values.secondaryVerticals.filter((v) => v !== vertical),
                                    });
                                  }}
                                  value={values.primaryVertical}
                                >
                                  {verticalOptions.map((vertical) => (
                                    <option key={vertical.name} value={vertical.name}>
                                      {vertical.label}
                                    </option>
                                  ))}
                                </Select>
                              </FieldWithLabel>
                            </Field>
                          </Col>
                        </Row>
                        <Row>
                          <Col lg={12} style={{ padding: '0 0 10px 0' }}>
                            <Field>
                              <FieldWithLabel label="Secondary Verticals">
                                {verticalOptions.map((vertical) => {
                                  const { secondaryVerticals } = values;
                                  const isPrimaryVertical = values.primaryVertical === vertical.name;
                                  const verticalId = String(vertical.name) as CreateOrganizationDTOSecondaryVerticalsEnum;
                                  const isSelected = secondaryVerticals.includes(verticalId);

                                  return (
                                    <Checkbox
                                      key={vertical.name}
                                      label={`${vertical.label}${isPrimaryVertical ? ' (Primary)' : ''}`}
                                      name={vertical.name}
                                      isDisabled={isPrimaryVertical}
                                      isChecked={isSelected}
                                      onChange={(e) => {
                                        const newSecondaryVerticals = e.target.checked
                                          ? [...secondaryVerticals, verticalId]
                                          : secondaryVerticals.filter((v) => v !== verticalId);

                                        setFieldValue('secondaryVerticals', newSecondaryVerticals);
                                      }}
                                    />
                                  );
                                })}
                              </FieldWithLabel>
                            </Field>
                          </Col>
                        </Row>
                        <Row>
                          <Col lg={12} style={{ padding: 0 }}>
                            <Field>
                              <FieldWithLabel
                                label="Reposit Sales Owner *"
                                touched={touched.salesOwnerId}
                                error={errors.salesOwnerId}
                              >
                                <Sort
                                  label="salesOwnerId"
                                  onSelect={(val) => setFieldValue('salesOwnerId', val)}
                                  options={[{ value: 'none', label: 'None' }, ...internalTeamMemberSalesOptions]}
                                  selected={values.salesOwnerId || ''}
                                  hideLabel
                                />
                              </FieldWithLabel>
                            </Field>
                          </Col>
                        </Row>
                      </Container>
                    </RepositCard>
                  </Col>

                  {!isPrivateLandlord ? (
                    <Col lg={12}>
                      <RepositCard title="Parent">
                        <P1>
                          Link this organisation to its parent and ultimate parent. Always check for an existing one, and only
                          create a new parent if necessary.
                        </P1>
                        <Container fluid>
                          <Row>
                            <Col md={6} style={{ padding: 0 }}>
                              <Field>
                                <FieldWithLabel
                                  label="Parent *"
                                  touched={touched.organizationGroupId}
                                  error={errors.organizationGroupId}
                                >
                                  <Typeahead
                                    emptyValue={NO_ORGANIZATION_GROUP_VALUE}
                                    name="organizationGroupId"
                                    options={organizationGroups}
                                    selectedOption={typeaheadCreate.getSelectedOption(
                                      'organizationGroupId',
                                      values.organizationGroupId,
                                      organizationGroups.find((option) => option.id === values.organizationGroupId),
                                    )}
                                    placeholder="Not set"
                                    value={values.organizationGroupId}
                                    disabled={isCreateOrganizationGroupLoading}
                                    isLoading={isCreateOrganizationGroupLoading || isFetchOrganizationGroupsLoading}
                                    onChange={typeaheadCreate.onChange('organizationGroupId')}
                                    onSearch={searchOrganizationGroups}
                                    onCreateOption={typeaheadCreate.onCreateOption('organizationGroupId')}
                                    renderCreateOption={renderCreateOrganizationGroupOption}
                                  />
                                </FieldWithLabel>
                              </Field>
                            </Col>
                            <Col md={6} style={{ padding: 0 }}>
                              <Field>
                                <FieldWithLabel
                                  label="Ultimate parent *"
                                  touched={touched.parentOrganizationGroupId}
                                  error={errors.parentOrganizationGroupId}
                                >
                                  <Typeahead
                                    emptyValue={NO_ORGANIZATION_GROUP_VALUE}
                                    name="parentOrganizationGroupId"
                                    options={organizationGroups}
                                    selectedOption={typeaheadCreate.getSelectedOption(
                                      'parentOrganizationGroupId',
                                      values.parentOrganizationGroupId,
                                      organizationGroups.find((option) => option.id === values.parentOrganizationGroupId),
                                    )}
                                    placeholder="Not set"
                                    value={values.parentOrganizationGroupId}
                                    disabled={isCreateOrganizationGroupLoading}
                                    isLoading={isCreateOrganizationGroupLoading || isFetchOrganizationGroupsLoading}
                                    onChange={typeaheadCreate.onChange('parentOrganizationGroupId')}
                                    onSearch={searchOrganizationGroups}
                                    onCreateOption={typeaheadCreate.onCreateOption('parentOrganizationGroupId')}
                                    renderCreateOption={renderCreateOrganizationGroupOption}
                                  />
                                </FieldWithLabel>
                              </Field>
                            </Col>
                          </Row>
                        </Container>
                      </RepositCard>
                    </Col>
                  ) : null}

                  <Col lg={12}>
                    <RepositCard title="Organisation Admin Details" subtitle="This is the main user on the account">
                      <Container fluid>
                        <Row>
                          <Col lg={12} style={{ padding: 0 }}>
                            <Field>
                              <FieldWithLabel label="Email address *" touched={touched.email} error={errors.email}>
                                <Input
                                  name="email"
                                  value={values.email}
                                  error={errors.email}
                                  touched={touched.email}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                />
                              </FieldWithLabel>
                            </Field>
                          </Col>
                        </Row>
                      </Container>
                    </RepositCard>
                  </Col>
                </Row>
                <Row>
                  <Col lg={12}>
                    <Actions>
                      <ActionInfo>Creating an organisation will immediately email the admin on this account</ActionInfo>
                      <Button buttonType="primary" disabled={!isValid}>
                        Create Organisation
                      </Button>
                    </Actions>
                  </Col>
                </Row>
              </Container>
            </form>
          );
        }}
      </Formik>
      {pendingOrganization ? (
        <ConfirmationModal
          title={`Send API keys to ${pendingIntegratorName}?`}
          text={`This will onboard the user for ${pendingIntegratorName} and send API credentials for this organisation. Please confirm the partner is correct.`}
          onDismiss={() => setPendingOrganization(undefined)}
          onSubmit={() => {
            dispatch(createOrganizationRequested(pendingOrganization));
            setPendingOrganization(undefined);
          }}
        />
      ) : null}
    </>
  );
};

export default AddNewOrganisation;
